import * as THREE from 'three';

export default class AnimationManager {
    constructor(model, animationConfig = null, modelAnimationName = null) {
        this.model = model;
        this.animationConfig = animationConfig;
        this.modelAnimationName = modelAnimationName;
        this.mixer = null;
        this.customActions = {};
        this.activeAction = null;
        this.fps = 30;
        this.currentClipIndex = 0;
        this.clipNames = [];
        
        // Callback appelé quand l'animation d'intro (premier clip "once") est terminée
        this.onIntroComplete = null;
        this.introCompleted = false;
        
        //console.log("AnimationManager initialisé");
        
        if (model && model.animations && model.animations.length > 0) {
            this.mixer = new THREE.AnimationMixer(model);
            
            //console.log('Animations disponibles:', model.animations.map(a => a.name));
            
            // Chercher l'animation spécifiée par animationName ou prendre la première
            let targetAnimation = null;
            if (this.modelAnimationName) {
                targetAnimation = model.animations.find(anim => anim.name === this.modelAnimationName);
                if (!targetAnimation) {
                    console.warn(`Animation "${this.modelAnimationName}" non trouvée, utilisation de la première animation`);
                    targetAnimation = model.animations[0];
                }
            } else {
                targetAnimation = model.animations[0];
            }
            
            //console.log(`Animation utilisée: ${targetAnimation.name}, durée: ${targetAnimation.duration}s`);
            
            // Créer automatiquement des clips custom basés sur l'animation
            this._extractAndCreateClips(targetAnimation);
            
                   
            // Lancer le premier clip
            this._playFirst();
        }
    }

   
    _extractAndCreateClips(animation) {
        const totalFrames = Math.floor(animation.duration * this.fps);
        //console.log(`Durée totale: ${animation.duration}s = ${totalFrames} frames à ${this.fps}fps`);
        
        // Utiliser la configuration d'animations depuis les settings ou valeurs par défaut
        let clips = [];
        
        if (this.animationConfig && Array.isArray(this.animationConfig)) {
            // Utiliser la configuration depuis les settings
            clips = this.animationConfig.map((animConfig, index) => ({
                name: animConfig.clipName || `${animation.name}_clip_${index}`,
                startFrame: animConfig.startFrame || 0,
                endFrame: animConfig.endFrame || animConfig.EndFrame || totalFrames, // Support pour EndFrame aussi
                type: animConfig.type || 'once'
            }));
            //console.log('🎬 Utilisation de la configuration d\'animations depuis les settings:', clips);
        } else {
            // Pas de configuration = pas d'animation automatique
            console.log('🎬 Aucune configuration d\'animation fournie, pas de clip créé');
            return;
        }
        
        clips.forEach(clipDef => {
            const duration = (clipDef.endFrame - clipDef.startFrame) / this.fps;
            
            //console.log(`Création clip: ${clipDef.name} (frames ${clipDef.startFrame}-${clipDef.endFrame}, durée: ${duration.toFixed(2)}s)`);
            
            // CORRIGER : Extraire TOUTES les keyframes dans la plage
            const customTracks = animation.tracks.map(track => {
                const startTimeTarget = clipDef.startFrame / this.fps;
                const endTimeTarget = clipDef.endFrame / this.fps;
                
                // Extraire toutes les keyframes dans la plage
                const newTimes = [];
                const newValues = [];
                const valueSize = track.values.length / track.times.length;
                
                for (let i = 0; i < track.times.length; i++) {
                    const time = track.times[i];
                    if (time >= startTimeTarget && time <= endTimeTarget) {
                        // Ajuster le temps pour commencer à 0
                        newTimes.push(time - startTimeTarget);
                        
                        // Copier les valeurs correspondantes
                        for (let j = 0; j < valueSize; j++) {
                            newValues.push(track.values[i * valueSize + j]);
                        }
                    }
                }
                
                // Si aucune keyframe dans la plage, créer au moins 2 points
                if (newTimes.length === 0) {
                    const startValues = this._getValuesAtTime(track, startTimeTarget);
                    const endValues = this._getValuesAtTime(track, endTimeTarget);
                    newTimes.push(0, duration);
                    newValues.push(...startValues, ...endValues);
                } else {
                    // S'assurer qu'on commence à 0 et qu'on finit à duration
                    if (newTimes[0] > 0) {
                        const startValues = this._getValuesAtTime(track, startTimeTarget);
                        newTimes.unshift(0);
                        newValues.unshift(...startValues);
                    }
                    if (newTimes[newTimes.length - 1] < duration) {
                        const endValues = this._getValuesAtTime(track, endTimeTarget);
                        newTimes.push(duration);
                        newValues.push(...endValues);
                    }
                }
                
                
                
                return new track.constructor(track.name, newTimes, newValues);
            });

            const customClip = new THREE.AnimationClip(clipDef.name, duration, customTracks);
        
            const action = this.mixer.clipAction(customClip);
            
            if (clipDef.type === 'loop') {
                action.setLoop(THREE.LoopRepeat, Infinity);
            } else {
                action.setLoop(THREE.LoopOnce);
                action.clampWhenFinished = true; // Reste sur la dernière frame
                
                // Ajouter un listener pour détecter la fin du clip
                this.mixer.addEventListener('finished', (event) => {
                    if (event.action === action) {
                        //console.log(`🏁 Clip "${clipDef.name}" terminé, passage au suivant`);
                        
                        // Appeler le callback onIntroComplete quand le premier clip "once" est terminé
                        if (!this.introCompleted && clipDef.type === 'once') {
                            this.introCompleted = true;
                            if (this.onIntroComplete) {
                                console.log(`🎬 Animation d'intro terminée, déclenchement du callback`);
                                this.onIntroComplete();
                            }
                        }
                        
                        this.nextClip();
                    }
                });
            }

            this.customActions[clipDef.name] = action;
        });
        
        this.clipNames = Object.keys(this.customActions);
       // console.log('Clips custom créés:', this.clipNames);
    }

    // Nouvelle méthode pour extraire les valeurs à un temps donné
    _getValuesAtTime(track, targetTime) {
        const times = track.times;
        const values = track.values;
        const valueSize = values.length / times.length;
        
        // Si le temps est exactement sur une keyframe
        for (let i = 0; i < times.length; i++) {
            if (Math.abs(times[i] - targetTime) < 0.001) {
                const result = [];
                for (let j = 0; j < valueSize; j++) {
                    result.push(values[i * valueSize + j]);
                }
                return result;
            }
        }
        
        // Sinon, interpoler entre deux keyframes
        let i1 = 0, i2 = times.length - 1;
        for (let i = 0; i < times.length - 1; i++) {
            if (targetTime >= times[i] && targetTime <= times[i + 1]) {
                i1 = i;
                i2 = i + 1;
                break;
            }
        }
        
        // Interpolation linéaire
        const t1 = times[i1];
        const t2 = times[i2];
        const factor = (targetTime - t1) / (t2 - t1);
        
        const result = [];
        for (let j = 0; j < valueSize; j++) {
            const v1 = values[i1 * valueSize + j];
            const v2 = values[i2 * valueSize + j];
            result.push(v1 + (v2 - v1) * factor);
        }
        
        return result;
    }

    _playFirst() {
        this.currentClipIndex = 0;
        this._playClipAtIndex(this.currentClipIndex);
    }

    _playClipAtIndex(index) {
        if (index >= 0 && index < this.clipNames.length) {
            const clipName = this.clipNames[index];
            const action = this.customActions[clipName];
            
            //console.log(`▶️ Joue clip ${index + 1}/${this.clipNames.length}: ${clipName}`);
            
            // Arrêter l'action précédente
            if (this.activeAction) {
                this.activeAction.stop();
            }
            
            // Jouer la nouvelle action
            this.activeAction = action;
            action.reset();
            action.play();
            
            //console.log(`🎬 Playing: ${clipName} (durée: ${action.getClip().duration.toFixed(2)}s)`);
        }
    }

    nextClip() {
        const nextIndex = this.currentClipIndex + 1;
        
        // Vérifier s'il y a un clip suivant
        if (nextIndex < this.clipNames.length) {
            this.currentClipIndex = nextIndex;
            //console.log(`⏭️ Passage au clip suivant (${this.currentClipIndex + 1}/${this.clipNames.length})`);
            this._playClipAtIndex(this.currentClipIndex);
        } else {
            //console.log(`🏁 Fin de la séquence d'animations (${this.clipNames.length}/${this.clipNames.length})`);
            // Ne pas rejouer, l'animation reste sur la dernière frame (clampWhenFinished = true)
        }
    }

    // Méthode pour jouer un clip spécifique par son index
    playClipByIndex(index) {
        if (index >= 0 && index < this.clipNames.length) {
            this.currentClipIndex = index;
            this._playClipAtIndex(this.currentClipIndex);
            //console.log(`🎬 Animation lancée au clip index ${index}: ${this.clipNames[index]}`);
        } else {
            console.warn(`Index de clip invalide: ${index}. Clips disponibles: 0-${this.clipNames.length - 1}`);
        }
    }

    update(delta) {
        if (this.mixer) {
            this.mixer.update(delta);
        }
    }

    stop() {
        if (this.activeAction) {
            this.activeAction.stop();
            this.activeAction = null;
        }
    }

    // Méthode pour nettoyer les event listeners
    dispose() {
        this.stop();
        // Supprimer l'event listener si besoin
        document.removeEventListener('keydown', this._keyHandler);
    }
}